<template>
  <NotificationTemplate
    :notification="notification"
    class="notification__update-demo-video"
  >
    <template #pic>
      <Avatar
        :src="initiatorAvatarSrc"
        :gap="initiatorInitials"
      />
    </template>
    <template #title>
      <BaseSubHeading
        size="large"
        color="monochrome-06"
        darkColor="monochrome-03"
        weight="400"
      >
        <span class="notification__update-demo-video__initiator">
          {{ notification.initiatorFullName }}
        </span>
        uploaded Demo Video to
        <a
          class="link is-large"
          target="_blank"
          :href="notification.itemUrl"
        >
          Item {{ notification.itemSequenceNumber }}:
        </a>
      </BaseSubHeading>
    </template>
    <template #content>
      <VideoPreview
        v-if="hasVideo"
        :playerId="String(notification.id)"
        :videoUrl="demoVideoUrl"
        :fileMetadata="demoAttachment"
        :projectId="projectId"
        :thumbnailUrl="demoThumbnailUrl"
        class="notification__update-demo-video__player"
      />
    </template>
  </NotificationTemplate>
</template>

<script setup lang="ts">
import { computed } from "vue"

import Avatar from "@/components/Avatar/index.vue"
import NotificationTemplate from "@/components/Notification/NotificationTemplate.vue"
import VideoPreview from "@/components/VideoPlayer/VideoPreview.vue"

import { FileMetaData } from "@/models"
import { NotificationItem } from "@/models/Notification/NotificationItem"
import { getInitials } from "@/utils/helpers"

const props = defineProps({
  notification: {
    type: NotificationItem,
    required: true
  }
})

const notificationContent = computed(() => props.notification.content)

const demoVideoUrl = computed(
  () =>
    notificationContent.value?.demoVideoUrl ||
    notificationContent.value?.item?.demoFile?.demoVideoUrl ||
    ""
)

const initiator = computed(
  () => notificationContent.value?.initiator ?? notificationContent.value?.initiators?.[0]
)

const initiatorAvatarSrc = computed(() => initiator.value?.picture?.pictureUrl ?? null)

const initiatorInitials = computed(() => getInitials(props.notification.initiatorFullName))

const demoAttachment = computed<FileMetaData | null>(() => {
  const attachments = notificationContent.value?.demoAttachmentsMetadata?.attachments
  if (attachments?.length) return attachments[0]
  return null
})

const demoThumbnailUrl = computed(
  () =>
    notificationContent.value?.demoVideoThumbnailUrl ||
    notificationContent.value?.demoAttachmentsMetadata?.attachments?.[0]?.url ||
    ""
)

const projectId = computed(
  () => notificationContent.value?.item?.projectId || notificationContent.value?.projectId || ""
)

const hasVideo = computed(() => Boolean(demoVideoUrl.value || demoAttachment.value?.id))
</script>

<style lang="scss" scoped>
.notification__update-demo-video {
  &__initiator {
    font-weight: 500;
  }

  &__player {
    width: 190px;
    height: 108px;
    margin-top: var(--step2);

    @media (max-width: 1536px) {
      width: 162px;
      height: 90px;
    }

    :deep(.video-preview) {
      width: 100%;
      height: 100%;
    }
  }
}
</style>
